// utils/clientCrypto.ts

/**
 * Deriva uma chave AES-GCM de 256 bits a partir da sesskey usando SHA-256.
 */
async function getCryptoKey(sesskey: string): Promise<CryptoKey> {
  const enc = new TextEncoder();
  // Transforma a sesskey (geralmente 10 chars) em um hash forte de 32 bytes
  const keyMaterial = await window.crypto.subtle.digest('SHA-256', enc.encode(sesskey));

  return window.crypto.subtle.importKey(
    'raw',
    keyMaterial,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Criptografa o token usando a sesskey do Moodle.
 * Retorna uma string em Base64 contendo o Vetor de Inicialização (IV) e o Texto Cifrado.
 */
export async function encryptTokenWithSesskey(token: string, sesskey: string): Promise<string> {
  const enc = new TextEncoder();
  const key = await getCryptoKey(sesskey);
  const iv = window.crypto.getRandomValues(new Uint8Array(12)); // IV aleatório obrigatório no AES-GCM

  const ciphertext = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    enc.encode(token)
  );

  // Junta o IV (12 bytes) com o Ciphertext para salvar no cookie
  const bundle = new Uint8Array(iv.length + ciphertext.byteLength);
  bundle.set(iv, 0);
  bundle.set(new Uint8Array(ciphertext), iv.length);

  // Converte para Base64 (A conversão direta é segura porque o payload é minúsculo)
  return btoa(String.fromCharCode(...bundle));
}

/**
 * Tenta descriptografar o token do cookie usando a sesskey atual.
 * Se a sesskey estiver errada (sessão velha) ou o cookie corrompido, retorna null.
 */
export async function decryptTokenWithSesskey(base64Bundle: string, sesskey: string): Promise<string | null> {
  try {
    const bundle = new Uint8Array(atob(base64Bundle).split('').map(c => c.charCodeAt(0)));
    const iv = bundle.slice(0, 12);
    const ciphertext = bundle.slice(12);

    const key = await getCryptoKey(sesskey);

    const decrypted = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ciphertext
    );

    return new TextDecoder().decode(decrypted);
  } catch (e) {
    // Falha na descriptografia (Sesskey diferente ou dado corrompido)
    return null;
  }
}